﻿import { Component, Inject } from "@fiqas/angular-utils";
import { Utils } from "@fiqas/utils";
import * as Rx from "rxjs/Rx";
import { FIQAssist } from "../../app";
import { DropdownEntry } from "../../controls/dropdownbox";
import { IDisposable } from "../../interfaces/general";
import { CollectionResult, IIdentityLink } from "../../interfaces/iFqs";
import {
    BalanceReportParameters,
    ReportAngle,
    ReportCollection,
    ReportCollectionItem,
    ReportParameters,
    ReportPeriod,
    ReportType,
} from "../../models/recordReport";
import { CaseType, CompactServiceCase } from "../../models/serviceCase";
import { AuthorizationService } from "../../services/authorizationService";
import { CacheService } from "../../services/cache";
import { DialogService } from "../../services/dialogService";
import { ExportService } from "../../services/exportToExcelService";
import { HomeService } from "../../services/fiqassistApi/homeService";
import { ReportService } from "../../services/fiqassistApi/reportService";
import { IsProcessingService } from "../../services/isProcessingService";
import { LogService } from "../../services/logService";
import { ServiceUtils } from "../../services/serviceBase";
import { TranslationService } from "../../services/TranslationService";
import { Application } from "../application";

type ChartType =
    | "PieChart"
    | "AreaChart"
    | "AnnotationChart"
    | "BarChart"
    | "Gauge"
    | "LineChart";
interface ChartSettings {
    options: {
        title: string;
        slices?: { [index: number]: { color: string } };
        series?: {
            [index: number]: {
                color?: string;
                type?: string;
                visibleInLegend?: boolean;
                pointSize?: number;
            };
        };
        hAxis?: {
            ticks?: any[];
            format?: string;
        };
        vAxis?: {
            ticks?: any[];
            format?: string;
        };
        isStacked?: boolean;
    };
    type: ChartType;
    data?: ChartData;
    view?: { columns: (Number | ChartColumn)[] };
}

interface ChartColumn {
    id?: string;
    label?: string;
    type: string;
    roll?: string;
    sourceColumn?: number;
}
interface ChartRowDefinition {
    c: { v: any; records?: () => IIdentityLink }[];
}
interface ChartRow extends ChartRowDefinition {
    records?: () => IIdentityLink;
}
interface ChartData {
    id: IIdentityLink;
    cols: ChartColumn[];
    rows: ChartRow[];
}
interface ReportSettings {
    reportType: ReportType;
    fromDate: Date;
    toDate: Date;
    reportAngle: ReportAngle;
    caseTypes: CaseType[];
    recordLink: IIdentityLink;
    activeOnly: boolean;
    caseId?: number;
}

enum CaseTypeSelector {
    BUG = 1 << 0,
    REQUEST = 1 << 1,
    BUGANDREQUEST = 1 << 2,
}

@Component({
    module: FIQAssist,
    selector: Reports.selector,
    templateUrl: "views/reports.html",
    require: {
        application: "^application",
    },
})
export class Reports {
    public static selector = "reports";
    public highlightMenuItem = "reports";
    private static Angles: { value: ReportAngle; desc: string }[] = [
        { value: "status", desc: "Status" },
        { value: "severity", desc: "Severity" },
        { value: "ownership", desc: "Ownership" },
        { value: "createdBy", desc: "CreatedBy" },
        { value: "customer", desc: "Customer" },
    ];
    private static CauseAngle: { value: ReportAngle; desc: string } = { value: "cause", desc: "Cause" };

    private static Periods: DropdownEntry<ReportPeriod>[] = [
        { key: "year", text: "Year" },
        { key: "month", text: "Month" },
        { key: "week", text: "Week" },
        { key: "day", text: "Day" },
    ];

    private static CaseTypesMapping: {
        value: CaseTypeSelector;
        caseType: CaseType[];
        desc: string;
    }[] = [
        { value: CaseTypeSelector.BUG, caseType: ["bug"], desc: "Bug" },
        {
            value: CaseTypeSelector.REQUEST,
            caseType: ["request"],
            desc: "Request",
        },
        {
            value: CaseTypeSelector.BUGANDREQUEST,
            caseType: ["bug", "request"],
            desc: "All",
        },
    ];

    public _caseId?: number;
    public get caseId() {
        return this._caseId;
    }
    public set caseId(caseId: string | number | undefined) {
        if (typeof caseId == "string") {
            this._caseId = parseInt(caseId);
            return;
        }
        this._caseId = caseId;
    }

    private application: Application;
    constructor(
        @Inject() private authorizationService: AuthorizationService,
        @Inject() private reportService: ReportService,
        @Inject() private homeService: HomeService,
        @Inject() private logService: LogService,
        @Inject() private isProcessingService: IsProcessingService,
        @Inject() private translationService: TranslationService,
        @Inject() private dialogService: DialogService,
        @Inject() private cacheService: CacheService,
        @Inject("$location") private $location: ng.ILocationService,
        @Inject("$timeout") private $timeout: ng.ITimeoutService,
        @Inject("$scope") private $scope: ng.IScope,
        @Inject() private exportService: ExportService,
        @Inject("$element") private $element: angular.IRootElementService
    ) {}

    public static $canActivate = AuthorizationService.RoleCheck;

    public async $routerOnActivate(
        next?: angular.ComponentInstruction,
        prev?: angular.ComponentInstruction
    ) {
        await this.authorizationService.roleLoaded;
        var settings = this.cacheService.getValue<ReportSettings>(
            "current.report.values"
        );
        if (settings) {
            this.reportType = settings.reportType;
            this.selectedReportAngle = settings.reportAngle;
            this.selectedCaseTypes = Reports.CaseTypesMapping.filter(
                (typeMap) =>
                    typeMap.caseType.every((ct) =>
                        settings.caseTypes.some((cct) => cct === ct)
                    ) &&
                    settings.caseTypes.every((ct) =>
                        typeMap.caseType.some((cct) => cct === ct)
                    )
            )[0];
            this._reportFromDate = settings.fromDate;
            this._reportToDate = settings.toDate;
            this.activeCasesOnly = settings.activeOnly;
            this.caseId = settings.caseId;
            await this.$timeout();
            await this.updateReport();
            if (settings.recordLink) {
                this.currentRecordLink = settings.recordLink;
                this.fillGrid(settings.recordLink);
            }
        }
    }

    public async $routerOnReuse(
        next?: angular.ComponentInstruction,
        prev?: angular.ComponentInstruction
    ) {
        await this.authorizationService.roleLoaded;
    }

    public SearchResultCount: number;

    $onInit() {
        this.disposables.push(
            this.application.addOnAccountsChanged(() => {
                if (this.reportType == "active" || this.chartData != null)
                    this.updateReport();
            })
        );
        if (this.reportType == "active") this.updateReport();
        if (
            this.authorizationService.currentRoleIn(["SD", "FQS"]) &&
            Reports.Angles.indexOf(Reports.CauseAngle) == -1
        ) {
            Reports.Angles.push(Reports.CauseAngle);
        }
    }

    private initActiveReport() {}

    private initProjectReport() {}

    private initHistoricReport() {
        var to = Date.today();
        to.setMonth(to.getMonth() + 1, 1);
        to = to.addDays(-1);

        this._reportToDate = to.clone();

        var from = Date.today();
        from.setDate(1);
        from.setFullYear(from.getFullYear() - 1);

        this._reportFromDate = from.clone();
    }

    private initBalanceReport() {
        var to = Date.today();
        to.setMonth(to.getMonth() + 1, 1);
        to = to.addDays(-1);
        this._reportToDate = to.clone();
        this.setDateForBalancePeriod();
    }

    private setDateForBalancePeriod() {
        let to = new Date();
        let from: Date;
        to.setHours(0, 0, 0, 0);
        switch (this.selectedReportPeriod[0]) {
            case "year": //huidig + vorig jaar (volledig)
                to.setMonth(11, 31);
                this._reportToDate = to;
                from = to.addDays(1);
                from.setFullYear(from.getFullYear() - 2);
                break;
            case "month": // jaar terug vanaf huidige maand einde
                to.setMonth(to.getMonth() + 1, 0);
                this._reportToDate = to;
                from = to.addDays(1);
                from.setFullYear(from.getFullYear() - 1);
                break;
            case "week": // maand terug tot/met komende zondag
                to.setDate(to.getDate() + (to.getDay() % 7) - 1);
                this._reportToDate = to;
                from = to.addDays(-28);
                break;
            case "day":
                from = to.addDays(1);
                this._reportToDate = Date.today();
                from = this._reportToDate.clone();
                from.setDate(from.getDate() - 7);
                break;
        }
        this._reportFromDate = from.clone();
    }

    private disposables: IDisposable[] = [];
    $onDestroy() {
        this.cacheService.setValue<ReportSettings>("current.report.values", {
            reportType: this.reportType,
            fromDate: this._reportFromDate,
            toDate: this._reportToDate,
            activeOnly: this.activeCasesOnly,
            reportAngle: this.selectedReportAngle,
            caseTypes: this.selectedCaseTypes.caseType,
            recordLink: this.currentRecordLink,
        });
        this.disposables.forEach((d) => {
            try {
                d();
            } catch (e) {
                this.logService.warning("Reports", "Error disposing", e);
            }
        });
        this.currentGridSubscription = null;
        this.currentSearch = null;
    }

    private _currentSearchSubscription: Rx.Subscription;
    private get currentSearch(): Rx.Subscription {
        return this._currentSearchSubscription;
    }
    private set currentSearch(subscription: Rx.Subscription) {
        if (this._currentSearchSubscription) {
            this._currentSearchSubscription.unsubscribe();
        }
        this._currentSearchSubscription = subscription;
    }

    public reportTypes: DropdownEntry<ReportType>[] = [
        { key: "active", text: "REPORT_ACTIVE_CASES" },
        { key: "project", text: "REPORT_PROJECT_CASES" },
        { key: "balance", text: "REPORT_BALANCE" },
        { key: "historic", text: "REPORT_HISTORIC_CASES" },
    ];
    public reportTypeSelected(type: ReportType[]) {
        this.reportType = type.shift();
        this.chartData = null;
        this.caseResult = null;
        this.currentSearch = null;
        this.currentGridSubscription = null;
        this.SearchResultCount = null;
        switch (this.reportType) {
            case "active":
                this.initActiveReport();
                this.updateReport();
                break;
            case "project":
                this.initProjectReport();
                this.updateReport();
                break;
            case "historic":
                this.initHistoricReport();
                break;
            case "balance":
                this.initBalanceReport();
                break;
        }
    }
    private _reportType: ReportType = "active";
    private get reportType() {
        return this._reportType;
    }
    private set reportType(val: ReportType) {
        this._reportType = val;
        this.selectedReportType = val ? [val] : [];
        this._data = null;
    }
    public selectedReportType = ["active"];

    public get caseTypeMappings() {
        return Reports.CaseTypesMapping;
    }
    private selectedCaseTypes: {
        value: CaseTypeSelector;
        caseType: CaseType[];
        desc: string;
    } = Reports.CaseTypesMapping[2];
    public caseTypeSelected(caseTypeSelected: {
        value: CaseTypeSelector;
        caseType: CaseType[];
        desc: string;
    }) {
        this.selectedCaseTypes = caseTypeSelected;
        if (this.reportType == "active" || this.chartData != null)
            this.updateReport();
    }

    public get reportPeriods() {
        return Reports.Periods;
    }
    private selectedReportPeriod: ReportPeriod[] = ["month"];
    public reportPeriodChanged(period: ReportPeriod[]) {
        this.selectedReportPeriod = period || [];
        this.setDateForBalancePeriod();
    }

    public get reportAngles() {
        return Reports.Angles;
    }
    private selectedReportAngle: ReportAngle = "status";
    public reportAngleChanged(angle: ReportAngle) {
        this.selectedReportAngle = angle;
        if (this.reportType == "active" || this.chartData != null)
            this.updateReport();
    }

    private chartData: ChartData;

    private _reportToDate: Date;
    public get reportToDate() {
        return this._reportToDate;
    }
    public reporttodatechanged(date: Date) {
        if (date) date.setHours(0, 0, 0, 0);
        this._reportToDate = date;
    }

    private _reportFromDate: Date;
    public get reportFromDate() {
        return this._reportFromDate;
    }
    public reportfromdatechanged(date: Date) {
        if (date) date.setHours(0, 0, 0, 0);
        this._reportFromDate = date;
    }

    private _maxFromDate: Date;
    public get maxFromDate(): Date {
        if (!this._reportToDate) {
            let m = Date.today();
            m.setHours(0, 0, 0, 0);
            m = m.addDays(-1);
            return m.equals(this._maxFromDate)
                ? this._maxFromDate
                : (this._maxFromDate = m);
        } else {
            let m = Date.today();
            m.setHours(0, 0, 0, 0);
            if (this._reportToDate <= m) {
                m = this._reportToDate;
            }
            return m.equals(this._maxFromDate)
                ? this._maxFromDate
                : (this._maxFromDate = m);
        }
    }
    private _minToDate: Date;
    public get minToDate(): Date {
        if (this._reportFromDate) {
            var m = this._reportFromDate;
            return m.equals(this._minToDate)
                ? this._minToDate
                : (this._minToDate = m);
        }
        return null;
    }
    private _maxToDate: Date;
    public get maxToDate(): Date {
        var d = Date.today();
        d.setHours(0, 0, 0, 0);
        d.setMonth(d.getMonth() + 1, 1);
        d = d.addDays(-1);
        return d.equals(this._maxToDate)
            ? this._maxToDate
            : (this._maxToDate = d);
    }

    private _maxToDateBalanceReport: Date;
    public get maxToDateBalanceReport(): Date {
        var d = Date.today();
        d.setHours(0, 0, 0, 0);
        d.setMonth(11, 31);
        return d.equals(this._maxToDateBalanceReport)
            ? this._maxToDateBalanceReport
            : (this._maxToDateBalanceReport = d);
    }

    private activeCasesOnly: boolean;
    public activeCasesOnlyChecked(active: boolean) {
        this.activeCasesOnly = active;
    }

    private newOnTop: boolean = true;
    public newOnTopChecked(active: boolean) {
        this.newOnTop = active;
        if (this._data) this.load(this._data);
    }

    private displayBalanceValues: boolean = true;
    public onDisplayBalanceValues(active: boolean) {
        this.displayBalanceValues = active;
        if (this._data) this.load(this._data);
    }

    private async reportUrl(reportType: ReportType) {
        var start = await this.homeService.StartResult;
        var url: IIdentityLink;
        switch (this.reportType) {
            case "active":
                url = start.reportActiveCases;
                break;
            case "project":
                url = start.reportProjectCases;
                break;
            case "historic":
                url = start.reportHistoricCases;
                break;
            case "balance":
                url = start.balanceReport;
                break;
        }
        return url.fullHref.replace(/{.*}/, "");
    }

    public async updateReport() {
        if (
            !this.reportType ||
            !this.selectedCaseTypes ||
            this.application.selectedCustomers.length == 0
        )
            return;
        if (
            (this.reportType == "historic" || this.reportType == "balance") &&
            (!this._reportFromDate || !this.reportToDate)
        )
            return;
        if (
            this.reportType == "project" &&
            (!this.caseId || this.caseId < 10000)
        )
            return;
        try {
            this.isProcessingService.StartProcessing();

            this.currentRecordLink = null;
            var reportUrl = "";
            this.caseResult = null;
            var cid = this.application.selectedCustomers.map((k) => k.key);
            this.currentSearch = null;
            this.currentGridSubscription = null;

            [reportUrl] = await Promise.all([
                this.reportUrl(this.reportType),
                this.translationService.onLoaded,
            ]);
        } catch (error) {
            if (ServiceUtils.IsUnauthorizedResponse(error)) {
                return;
            }
            this.application.setSeqUrl(error);
            this.logService.error(
                "Reports",
                "Error while getting report data",
                error
            );
            this.dialogService.showErrorDialog(
                "error",
                "ERROR",
                "REPORT_SEARCH_ERROR",
                { errorResult: error, translate: true }
            );
            return;
        } finally {
            this.isProcessingService.StopProcessing();
        }

        var parameters: ReportParameters | BalanceReportParameters;

        if (this.reportType == "active") {
            parameters = <ReportParameters>{
                angle: this.selectedReportAngle,
                caseType: this.selectedCaseTypes.caseType,
                cid: cid,
                activeCasesOnly: undefined,
                from: new Date(2016, 0, 1, 0, 0, 0, 0).toISODateString(),
                until: Date.today().addDays(1).toISODateString(),
            };
        } else if (this.reportType == "project") {
            parameters = <ReportParameters>{
                angle: this.selectedReportAngle,
                caseType: this.selectedCaseTypes.caseType,
                projectTreeOfNumber: this.caseId.toString(),
                cid: cid,
                activeCasesOnly: this.activeCasesOnly ? true : undefined,
                from: (this._reportFromDate
                    ? this._reportFromDate
                    : new Date(2016, 0, 1, 0, 0, 0, 0)
                ).toISODateString(),
                until: (this._reportToDate ? this._reportToDate : Date.today())
                    .addDays(1)
                    .toISODateString(),
            };
        } else if (this.reportType == "historic") {
            parameters = <ReportParameters>{
                angle: this.selectedReportAngle,
                caseType: this.selectedCaseTypes.caseType,
                cid: cid,
                activeCasesOnly: this.activeCasesOnly ? true : undefined,
                from: this._reportFromDate.toISODateString(),
                until: this._reportToDate.addDays(1).toISODateString(),
            };
        } else if (this.reportType == "balance") {
            parameters = <BalanceReportParameters>{
                from: this._reportFromDate.toISODateString(),
                until: this._reportToDate.addDays(1).toISODateString(),
                caseType:
                    this.selectedCaseTypes.caseType.length == 2
                        ? "all"
                        : this.selectedCaseTypes.caseType[0],
                period: this.selectedReportPeriod[0],
                caseState: "all",
                cid: cid,
            };
        }

        this.isProcessingService.StartProcessing();
        var sub = Rx.Observable.fromPromise(
            this.reportService.GetReport(reportUrl, parameters)
        ).subscribe({
            next: async (data) => this.load(data),
            error: (error) => {
                if (ServiceUtils.IsUnauthorizedResponse(error)) {
                    return;
                }
                this.application.setSeqUrl(error);
                this.logService.error(
                    "Reports",
                    "Error while getting report data",
                    error
                );
                this.dialogService.showErrorDialog(
                    "error",
                    "ERROR",
                    "REPORT_SEARCH_ERROR",
                    { errorResult: error, translate: true }
                );
            },
        });
        sub.add(() => this.isProcessingService.StopProcessing());
        this.currentSearch = sub;
    }
    public chartSettings: ChartSettings = {
        options: {
            title: "",
        },
        type: "PieChart",
        data: <ChartData>{
            id: null,
            cols: [],
            rows: [],
        },
    };

    private allLink = null;
    private _serviceCasesCount: number;
    public get serviceCasesCount(): number {
        return this._serviceCasesCount;
    }
    private _data: ReportCollection;
    public load(data: ReportCollection) {
        this._data = data;
        this.allLink = null;
        this.chartData = null;
        var settings = angular.copy(this.chartDefinitions[this.reportType]);
        var sortings = this.chartDefinitions.sortings[this.selectedReportAngle];
        var colors = this.chartDefinitions.colors[this.selectedReportAngle];
        this._serviceCasesCount = data.value;
        if (this.reportType == "active") {
            settings.options.title = this.translationService
                .Translate(
                    `CHART.${this.reportType}.${this.selectedReportAngle}.TITLE`
                )
                .replace(
                    "{count}",
                    Number(data.value).toLocaleFormattedString(
                        this.translationService.getLocal()
                    )
                );
            if (this.selectedReportAngle == "status") {
                this.chartData = this.parseActive(
                    data,
                    colors,
                    settings,
                    (a, b) =>
                        sortings[a[0].toLocaleLowerCase()] -
                        sortings[b[0].toLocaleLowerCase()]
                );
            } else {
                this.chartData = this.parseActive(data, colors, settings);
            }
        } else if (this.reportType == "project") {
            settings.options.title = this.translationService
                .Translate(
                    `CHART.${this.reportType}.${this.selectedReportAngle}.TITLE`
                )
                .replace(
                    "{count}",
                    Number(data.value).toLocaleFormattedString(
                        this.translationService.getLocal()
                    )
                );
            if (this.selectedReportAngle == "status") {
                this.chartData = this.parseActive(
                    data,
                    colors,
                    settings,
                    (a, b) =>
                        sortings[a[0].toLocaleLowerCase()] -
                        sortings[b[0].toLocaleLowerCase()]
                );
            } else {
                this.chartData = this.parseActive(data, colors, settings);
            }
        } else if (this.reportType == "historic") {
            settings.options.title = this.translationService
                .Translate(
                    `CHART.${this.reportType}.${this.selectedReportAngle}.TITLE`
                )
                .replace(
                    "{count}",
                    Number(data.value).toLocaleFormattedString(
                        this.translationService.getLocal()
                    )
                );
            if (this.selectedReportAngle == "status") {
                this.chartData = this.parseHistory(
                    data,
                    colors,
                    settings,
                    (b, a) =>
                        sortings[a[0].toLocaleLowerCase()] -
                        sortings[b[0].toLocaleLowerCase()]
                );
            } else {
                this.chartData = this.parseHistory(data, colors, settings);
            }
        } else if (this.reportType == "balance") {
            var period = this.reportPeriods
                .filter((p) => this.selectedReportPeriod[0] == p.key)
                .shift().text;
            settings.options.title = this.translationService
                .Translate(`CHART.BALANCE.TITLE`)
                .replace(
                    "{type}",
                    this.translationService.Translate(period).toLowerCase()
                );
            this.chartData = this.parseBalance(
                data,
                this.chartDefinitions.colors["balance"],
                settings
            );
        } else {
            throw `Unknown report type ${this.reportType}`;
        }
        settings.data = this.chartData;
        this.chartSettings = settings;
        this.allLink = data.records;
    }

    public parseBalance(
        data: ReportCollection,
        colors: { [column: string]: string },
        settings: ChartSettings
    ): ChartData {
        var dataset: ChartData = {
            id: data.id,
            cols: [{ id: "Month", label: "Date", type: "date" }],
            rows: [],
        };
        var months: {
            [month: string]: ReportCollectionItem & {
                [key: string]: ReportCollectionItem;
            };
        } = {};

        var columnKeys: [string, string][] = Object.getOwnPropertyNames(
            data.items.reduce((p, c, i, a) => {
                months[c.desc] = <any>c;
                c.items.forEach((i) => {
                    p[i.desc] = true;
                    months[c.desc][i.desc] = i;
                });
                return p;
            }, {})
        ).map<[string, string]>((c) => {
            var t = this.translationService.Translate(c);
            return [c, t[0].toUpperCase() + t.substr(1)];
        });

        if (columnKeys.length == 0) {
            columnKeys.push([
                "nodata",
                this.translationService.Translate("NO_DATA"),
            ]);
        } else {
            columnKeys.push([
                "Saldo",
                this.translationService.Translate("SALDO"),
            ]);
        }

        columnKeys.forEach((col) => {
            dataset.cols.push({
                id: col[0],
                label: col[1],
                type: "number",
            });
        });
        var ticks: Date[] = [];
        Utils.forEachKey(months, (month, key) => {
            var first = key.split("|")[0];
            var date = new Date(first);
            var row: ChartRow = {
                c: [{ v: <any>date, records: () => month.records }].concat(
                    columnKeys.map((k) => {
                        var item = k[0] == "Saldo" ? month : month[k[0]];
                        return item
                            ? {
                                  v: Number(item.value || 0),
                                  records: () => item.records,
                              }
                            : {
                                  v: 0,
                                  records: () => {
                                      return undefined;
                                  },
                              };
                    })
                ),
            };
            ticks.push(date);
            dataset.rows.push(row);
        });

        settings.options.hAxis = settings.options.hAxis || {};
        settings.options.hAxis.ticks = ticks;
        if (this.selectedReportPeriod[0] == "year")
            settings.options.hAxis.format = "yyyy";
        else if (this.selectedReportPeriod[0] == "day")
            settings.options.hAxis.format = "dd MMM yyyy";
        else if (this.selectedReportPeriod[0] == "week")
            settings.options.hAxis.format = "ww (yyyy)";
        else settings.options.hAxis.format = "MMM yyyy";
        settings.options.vAxis = settings.options.vAxis || {};
        settings.options.vAxis.format = "#;#";

        var periodToolTip = (t, r, c) => {
            var value = t.getValue(r, c);
            var prefix = "";
            switch (this.selectedReportPeriod[0]) {
                case "year":
                    var date = t.getValue(r, 0) as Date;
                    prefix = `${date.getFullYear()}: `;
                    break;
                case "month":
                    var date = t.getValue(r, 0) as Date;
                    prefix = `${(101 + date.getMonth())
                        .toString()
                        .substr(1)}-${date.getFullYear()}: `;
                    break;
                case "week":
                    var date = t.getValue(r, 0) as Date;
                    prefix = `${this.translationService.Translate(
                        "WEEK"
                    )} ${date.getWeekNumber()}: `;
                    break;
                case "day":
                    var date = t.getValue(r, 0) as Date;
                    prefix = `${date.toLocaleDateString()}: `;
                    break;
            }
            return `${prefix}${value}`;
        };

        var dataColumn0 = 0;
        var dataColumn1 = {
            type: "number",
            role: "data",
            label: dataset.cols[1].label,
            id: dataset.cols[1].id,
            calc: (t, r) => {
                var val = t.getValue(r, 1);
                return val == 0 ? null : val * (this.newOnTop ? 1 : -1);
            },
            format: "#;#",
            sourceColumn: 1,
        };
        if (colors && colors[dataColumn1.id.toLowerCase()])
            dataColumn1["color"] = colors[dataColumn1.id.toLowerCase()];
        var tooltipColumn1 = {
            calc: (t, r) => periodToolTip(t, r, 1),
            format: "#;#",
            type: "string",
            role: "tooltip",
        };
        var dummyDataColumn1 = {
            calc: (t, r) => ((t.getValue(r, 1) || 0) > 0 ? -0.01 : null),
            type: "number",
            role: "data",
        };
        var annotationColumn1 = {
            calc: (t, r) => {
                var val = t.getValue(r, 1);
                return val == 0 ? null : `${val}`;
            },
            sourceColumn: 1,
            type: "string",
            role: "annotation",
        };
        var dataColumn2 = {
            type: "number",
            role: "data",
            label: dataset.cols[2].label,
            id: dataset.cols[2].id,
            calc: (t, r) => {
                var val = t.getValue(r, 2);
                return val == 0 ? null : val * (this.newOnTop ? -1 : 1);
            },
            format: "#;#",
            sourceColumn: 2,
        };
        if (colors && colors[dataColumn2.id.toLowerCase()])
            dataColumn2["color"] = colors[dataColumn2.id.toLowerCase()];
        var tooltipColumn2 = {
            calc: (t, r) => periodToolTip(t, r, 2),
            format: "#;#",
            type: "string",
            role: "tooltip",
        };
        var dummyDataColumn2 = {
            calc: (t, r) => ((t.getValue(r, 2) || 0) > 0 ? -0.01 : null),
            type: "number",
            role: "data",
        };
        var annotationColumn2 = {
            calc: (t, r) => {
                var val = t.getValue(r, 2);
                return val == 0 ? null : `${val}`;
            },
            sourceColumn: 2,
            type: "string",
            role: "annotation",
        };
        var dataColumn3 = 3;
        var coloredDataColumn3 = {
            type: "number",
            role: "data",
            label: dataset.cols[3].label,
            id: dataset.cols[3].id,
            sourceColumn: 3,
        };
        if (colors && colors[coloredDataColumn3.id.toLowerCase()])
            coloredDataColumn3["color"] =
                colors[coloredDataColumn3.id.toLowerCase()];

        var annotationColumn3 = {
            calc: "stringify",
            sourceColumn: 3,
            type: "string",
            role: "annotation",
        };

        var columns: (ChartColumn | Number)[] = [dataColumn0];
        columns.push(dataColumn3);
        columns.push(coloredDataColumn3);
        if (columns[2]["color"])
            settings.options.series[1].color = columns[2]["color"];
        if (this.displayBalanceValues) columns.push(annotationColumn3);

        if (this.newOnTop) {
            columns.push(dataColumn2);
            columns.push(tooltipColumn2);
            columns.push(dummyDataColumn2);
            if (this.displayBalanceValues) {
                columns.push(annotationColumn2);
            }
        } else {
            columns.push(dataColumn1);
            columns.push(tooltipColumn1);
            columns.push(dummyDataColumn1);
            if (this.displayBalanceValues) {
                columns.push(annotationColumn1);
            }
        }
        if (columns[this.displayBalanceValues ? 4 : 3]["color"])
            settings.options.series[2].color =
                columns[this.displayBalanceValues ? 4 : 3]["color"];

        if (this.newOnTop) {
            columns.push(dataColumn1);
            columns.push(tooltipColumn1);
            if (this.displayBalanceValues) columns.push(annotationColumn1);
        } else {
            columns.push(dataColumn2);
            columns.push(tooltipColumn2);
            if (this.displayBalanceValues) columns.push(annotationColumn2);
        }
        if (columns[this.displayBalanceValues ? 8 : 6]["color"])
            settings.options.series[4].color =
                columns[this.displayBalanceValues ? 8 : 6]["color"];

        settings.options.series[1].pointSize = this.displayBalanceValues
            ? 22
            : 12;
        settings.view = { columns: columns };
        return dataset;
    }

    public parseHistory(
        data: ReportCollection,
        colors: { [column: string]: string },
        settings: ChartSettings,
        sort?: (a: [string, string], b: [string, string]) => number
    ): ChartData {
        var dataset: ChartData = {
            id: data.id,
            cols: [{ id: "Month", label: "Date", type: "date" }],
            rows: [],
        };
        var months: {
            [month: string]: ReportCollectionItem & {
                [key: string]: ReportCollectionItem;
            };
        } = {};
        var current = this._reportFromDate.clone();
        current.setDate(1);
        while (Date.compare(current, this._reportToDate) <= 0) {
            months[current.toISODateString().substring(0, 7)] = <any>{};
            current.setMonth(current.getMonth() + 1);
        }
        var columnKeys: [string, string][] = Object.getOwnPropertyNames(
            data.items.reduce((p, c, i, a) => {
                months[c.desc] = <any>c;
                c.items.forEach((i) => {
                    p[i.desc] = true;
                    months[c.desc][i.desc] = i;
                });
                return p;
            }, {})
        )
            .map<[string, string]>((c) => {
                var t = this.translationService.Translate(c);
                return [c, t[0].toUpperCase() + t.substr(1)];
            })
            .sort(
                sort ? sort : (a, b) => (a[1] < b[1] ? 1 : a[1] > b[1] ? -1 : 0)
            );

        if (columnKeys.length == 0) {
            columnKeys.push([
                "nodata",
                this.translationService.Translate("NO_DATA"),
            ]);
        }

        columnKeys.forEach((col) => {
            dataset.cols.push({
                id: col[0],
                label: col[1],
                type: "number",
            });
        });
        var ticks: Date[] = [];
        Utils.forEachKey(months, (month, key) => {
            var date = new Date(key + "-01");
            var row: ChartRow = {
                c: [{ v: <any>date, records: () => month.records }].concat(
                    columnKeys.map((k) => {
                        var item = month[k[0]];
                        return item
                            ? {
                                  v: Number(item.value || 0),
                                  records: () => item.records,
                              }
                            : {
                                  v: 0,
                                  records: () => {
                                      return undefined;
                                  },
                              };
                    })
                ),
            };
            ticks.push(date);
            dataset.rows.push(row);
        });
        if (colors) {
            var series: { [index: number]: { color: string } } = {};
            columnKeys.forEach(
                (k, i) => (series[i] = { color: colors[k[0].toLowerCase()] })
            );
            settings.options.series = series;
        }
        settings.options.hAxis = {
            ticks: ticks,
            format: "MMM yyyy",
        };
        return dataset;
    }
    public parseActive(
        data: ReportCollection,
        colors: { [column: string]: string },
        settings: ChartSettings,
        sort?: (
            a: [string, string, number, IIdentityLink],
            b: [string, string, number, IIdentityLink]
        ) => number
    ): ChartData {
        var columnKeys: string[] = [];
        var rows = [];
        data.items
            .map<[string, string, number, IIdentityLink]>((d) => {
                var t = this.translationService.Translate(d.desc);
                return [
                    d.desc,
                    t[0].toUpperCase() + t.substr(1),
                    Number(d.value),
                    d.records,
                ];
            })
            .sort(
                sort ? sort : (a, b) => (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0)
            )
            .forEach((d) => {
                rows.push({
                    c: [{ v: d[1] }, { v: d[2] }],
                    records: () => d[3],
                });
                columnKeys.push(d[0]);
            });

        var dataset: ChartData = {
            id: data.id,
            cols: [
                { id: "t", label: "label", type: "string" },
                { id: "v", label: "value", type: "number" },
            ],
            rows: rows,
        };
        if (colors) {
            var slices: { [index: number]: { color: string } } = {};
            columnKeys.forEach(
                (k, i) => (slices[i] = { color: colors[k.toLowerCase()] })
            );
            settings.options.slices = slices;
        }
        return dataset;
    }

    private currentRecordLink: IIdentityLink;
    public chartOnSelect(item?) {
        this.currentRecordLink = null;
        if (
            this.chartSettings.view &&
            this.chartSettings.view.columns &&
            this.chartSettings.view.columns[item.column]
        ) {
            var column = this.chartSettings.view.columns[item.column];
            if ((<ChartColumn>column).sourceColumn)
                item.column = (<ChartColumn>column).sourceColumn;
            else if (!isNaN(parseInt(column.toString()))) item.column = column;
        }
        if (this.chartData && item) {
            var x: { records?: () => IIdentityLink };
            if (item.row !== null) {
                if (item.column === null) {
                    x = this.chartData.rows[item.row];
                } else {
                    x = this.chartData.rows[item.row].c[item.column];
                }
            }

            var records = x && x.records ? x.records() : null;
            if (records) {
                this.currentRecordLink = records;
                this.fillGrid(records);
            }
        }
    }
    public titleOnSelect() {
        this.currentRecordLink = null;
        if (this.allLink) {
            this.currentRecordLink = this.allLink;
            this.fillGrid(this.allLink);
        }
    }

    private caseResult: CollectionResult<CompactServiceCase>;
    private noResults = [];
    public get serviceCases() {
        return this.caseResult ? this.caseResult.items : this.noResults;
    }

    private _currentGridSubscription: Rx.Subscription;
    private get currentGridSubscription(): Rx.Subscription {
        return this._currentGridSubscription;
    }
    private set currentGridSubscription(subscription: Rx.Subscription) {
        if (this._currentGridSubscription) {
            this._currentGridSubscription.unsubscribe();
        }
        this._currentGridSubscription = subscription;
    }

    private fillGrid(href: IIdentityLink) {
        this.isProcessingService.StartProcessing();
        var sub = Rx.Observable.fromPromise(
            this.reportService.GetReportCases(href.fullHref)
        ).subscribe({
            next: (result) => (this.caseResult = result),
            error: (error) => {
                if (ServiceUtils.IsUnauthorizedResponse(error)) {
                    return;
                }
                this.application.setSeqUrl(error);
                this.logService.error(
                    "Reports",
                    "Error retrieving cases for report",
                    error
                );
                this.dialogService.showErrorDialog(
                    "error",
                    "ERROR",
                    "REPORT_SEARCH_ERROR",
                    { errorResult: error, translate: true }
                );
            },
        });
        sub.add(() => this.isProcessingService.StopProcessing());
        this.currentGridSubscription = sub;
    }

    private chartDefinitions: { [chartType: string]: ChartSettings } & {
        colors: { [angle: string]: { [column: string]: string } };
    } & { sortings: { [angle: string]: { [column: string]: number } } };

    public createXlsx() {
        this.exportService.saveExcelFromJson(this.serviceCases, null);
    }
}
